export interface PreHydrationSnapshot {
  renderMode: 'hydrate' | 'client-render';
  /**
   * Values of `data-ssr-skeleton` attributes found in the server-rendered HTML.
   * A skeleton component that renders this attribute (with its logging context
   * as value) can be attributed to the server: if the value is in this set, the
   * skeleton was part of the SSR response, not a client-side replacement.
   */
  ssrSkeletons: ReadonlySet<string>;
  /**
   * Number of Suspense boundaries the server flagged for client rendering
   * (`<!--$!-->`), i.e. boundaries that errored during SSR.
   */
  ssrClientRenderBoundaries: number;
  /**
   * Number of boundaries still pending in the served HTML (`<!--$?-->`).
   * Expected to be 0 since SSR only pipes on `onAllReady`; kept as a canary.
   */
  ssrPendingBoundaries: number;
  ssrTotalBoundaries: number;
  ssrHadMarkupError: boolean;
}

declare global {
  interface Window {
    __TADARIDA_PRE_HYDRATION_SNAPSHOT__?: PreHydrationSnapshot;
    __TADARIDA_PRE_HYDRATION_SNAPSHOT_ERROR__?: string;
  }
}

/**
 * Records what the server-rendered HTML contained, before React mutates the
 * DOM. Must be called right before `hydrateRoot`/`createRoot` in the client
 * bootstrap. Idempotent: the first capture wins.
 *
 * The boundary comment markers (`$`, `$?`, `$!`) are React's internal fizz
 * format, verified against react-dom 19.1.2. If a React upgrade changes them,
 * the counts silently read 0 — this is best-effort instrumentation and must
 * never affect rendering.
 *
 * A capture that throws leaves no snapshot behind and records the reason
 * instead, so consumers can report why the attribution is missing.
 */
export const capturePreHydrationSnapshot = (config: {
  renderMode: 'hydrate' | 'client-render';
  container: Element;
}): void => {
  if (
    !process.browser ||
    window.__TADARIDA_PRE_HYDRATION_SNAPSHOT__ ||
    window.__TADARIDA_PRE_HYDRATION_SNAPSHOT_ERROR__
  ) {
    return;
  }

  try {
    const ssrSkeletons = new Set<string>();
    for (const element of document.querySelectorAll('[data-ssr-skeleton]')) {
      const value = element.getAttribute('data-ssr-skeleton');
      if (value) {
        ssrSkeletons.add(value);
      }
    }

    let ssrClientRenderBoundaries = 0;
    let ssrPendingBoundaries = 0;
    let ssrTotalBoundaries = 0;
    // NodeFilter.SHOW_COMMENT — numeric constant because the NodeFilter global
    // is not available in every environment this runs in (e.g. jsdom-in-node)
    const SHOW_COMMENT = 0x80;
    const walker = document.createTreeWalker(config.container, SHOW_COMMENT);
    while (walker.nextNode()) {
      const data = (walker.currentNode as Comment).data;
      if (data === '$!') {
        ssrClientRenderBoundaries++;
        ssrTotalBoundaries++;
      } else if (data === '$?') {
        ssrPendingBoundaries++;
        ssrTotalBoundaries++;
      } else if (data === '$') {
        ssrTotalBoundaries++;
      }
    }

    window.__TADARIDA_PRE_HYDRATION_SNAPSHOT__ = {
      renderMode: config.renderMode,
      ssrSkeletons,
      ssrClientRenderBoundaries,
      ssrPendingBoundaries,
      ssrTotalBoundaries,
      ssrHadMarkupError: Boolean(
        document.querySelector('[data-tadarida-server-markup-error]'),
      ),
    };
  } catch (error) {
    // best-effort instrumentation — must never break hydration, so the reason
    // is stored for the logs instead of being thrown or logged from here
    window.__TADARIDA_PRE_HYDRATION_SNAPSHOT_ERROR__ =
      error instanceof Error
        ? `${error.name}: ${error.message}`
        : 'unknown error';
  }
};

export const getPreHydrationSnapshot = (): PreHydrationSnapshot | undefined =>
  process.browser ? window.__TADARIDA_PRE_HYDRATION_SNAPSHOT__ : undefined;

/**
 * Set only when capturing the snapshot threw. Reported alongside the log that
 * misses its attribution, so the failure is visible instead of silent.
 */
export const getPreHydrationSnapshotError = (): string | undefined =>
  process.browser
    ? window.__TADARIDA_PRE_HYDRATION_SNAPSHOT_ERROR__
    : undefined;
